import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Clock, ShieldCheck, CheckCircle2, Loader2, Mail, Phone, MessageCircle } from "lucide-react";
import { LANGUAGES, CONTACT } from "@/data/content";
import { useLang } from "@/i18n";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { SectionLabel } from "./ui";
import { Reveal } from "./Reveal";
import { ScheduleCall } from "./ScheduleCall";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", church_name: "", role: "", email: "", phone: "", location: "",
  preferred_language: "English", preferred_contact: "Phone call",
  interest_device: true, interest_website: false,
  message: "", show_church_publicly: false,
};

const inputCls =
  "w-full bg-[#FAFAFC] border border-navy-100 rounded-xl px-4 py-3 min-h-[44px] text-charcoal-900 text-base placeholder:text-charcoal-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:border-transparent transition-shadow";

const Field = ({ id, label, required, error, children, hint }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-navy-900 mb-1.5">
      {label} {required && <span className="text-gold-600" aria-hidden>*</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1 text-xs text-charcoal-700/60">{hint}</p>}
    {error && <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>}
  </div>
);

export const ConsultationForm = () => {
  const { t } = useLang();
  const f = t.form;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState("");
  const started = useRef(false);

  const onStart = () => {
    if (!started.current) {
      started.current = true;
      trackEvent(EVENTS.FORM_STARTED);
    }
  };

  const set = (k) => (e) => setForm((fm) => ({ ...fm, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = f.errors.name;
    if (!form.church_name.trim()) err.church_name = f.errors.church;
    if (!form.interest_device && !form.interest_website) err.interest = f.errors.interest;
    if (!form.email.trim() && !form.phone.trim()) err.email = f.errors.contact;
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const interests = [
      form.interest_device && "Giving device",
      form.interest_website && "Church website",
    ].filter(Boolean).join(" + ");
    try {
      const { data } = await axios.post(`${API}/consultations`, { ...form, interest: interests });
      setLeadId(data.id);
      trackEvent(EVENTS.FORM_SUBMITTED);
      toast.success(f.toastOk);
    } catch {
      toast.error(f.toastErr);
    } finally {
      setSubmitting(false);
    }
  };

  const contactMethods = [
    CONTACT.phone && { icon: Phone, label: f.contactLabels.call, key: "call", href: `tel:${CONTACT.phone}` },
    CONTACT.sms && { icon: MessageCircle, label: f.contactLabels.text, key: "text", href: `sms:${CONTACT.sms}` },
    CONTACT.whatsapp && { icon: MessageCircle, label: f.contactLabels.whatsapp, key: "whatsapp", href: `https://wa.me/${CONTACT.whatsapp}` },
    CONTACT.email && { icon: Mail, label: f.contactLabels.email, key: "email", href: `mailto:${CONTACT.email}` },
  ].filter(Boolean);

  const benefitIcons = [Clock, HeartHandshake, ShieldCheck];

  const checkboxRow = (key, label, testid) => (
    <label className="flex items-center gap-3 rounded-xl border border-navy-100 bg-[#FAFAFC] px-4 py-3 min-h-[44px] cursor-pointer hover:border-gold-500/60 transition-colors">
      <input
        type="checkbox"
        checked={form[key]}
        onChange={(e) => setForm((fm) => ({ ...fm, [key]: e.target.checked }))}
        data-testid={testid}
        className="h-4 w-4 shrink-0 accent-[#D4AF37]"
      />
      <span className="text-base text-charcoal-700">{label}</span>
    </label>
  );

  return (
    <section id="contact" data-testid="consultation-section" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
        <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
          <SectionLabel>{f.label}</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-navy-900 leading-tight">{f.title}</h2>
          <p className="mt-4 text-base md:text-lg text-charcoal-700 leading-relaxed">{f.sub}</p>

          <ul className="mt-7 space-y-4">
            {f.benefits.map((it, i) => {
              const Icon = benefitIcons[i];
              return (
                <li key={it.t} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold-500/15 text-gold-600 shrink-0">
                    <Icon size={22} aria-hidden />
                  </span>
                  <div>
                    <p className="font-semibold text-navy-900">{it.t}</p>
                    <p className="text-sm text-charcoal-700/80">{it.d}</p>
                  </div>
                </li>
              );
            })}
          </ul>

          {contactMethods.length > 0 && (
            <div className="mt-7 rounded-2xl border border-navy-100 bg-[#FAFAFC] p-5">
              <p className="font-semibold text-navy-900">{f.prefer}</p>
              <div className="mt-3 flex flex-wrap gap-2.5">
                {contactMethods.map((m) => (
                  <a key={m.key} href={m.href} data-testid={`contact-${m.key}`}
                     className="inline-flex items-center gap-2 rounded-full border border-navy-200 bg-white px-4 py-2.5 min-h-[44px] text-sm font-semibold text-navy-900 hover:border-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500">
                    <m.icon size={16} aria-hidden /> {m.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="bg-white rounded-[1.5rem] border border-navy-100 shadow-xl p-6 md:p-10">
            {leadId ? (
              <div className="py-6" data-testid="consultation-success">
                <div className="text-center">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-500/15 text-gold-600">
                    <CheckCircle2 size={40} aria-hidden />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-navy-900">{f.successTitle}</h3>
                  <p className="mt-3 text-charcoal-700 leading-relaxed max-w-md mx-auto">
                    {f.successBody}
                    {form.email.trim() && ` ${f.successEmail}`}
                  </p>
                </div>
                <ScheduleCall consultationId={leadId} />
              </div>
            ) : (
              <form onSubmit={onSubmit} onFocus={onStart} className="space-y-5" data-testid="consultation-form" noValidate>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field id="f-name" label={f.labels.name} required error={errors.name}>
                    <input id="f-name" value={form.name} onChange={set("name")} className={inputCls} placeholder={f.placeholders.name} data-testid="input-name" aria-invalid={!!errors.name} autoComplete="name" />
                  </Field>
                  <Field id="f-church" label={f.labels.church} required error={errors.church_name}>
                    <input id="f-church" value={form.church_name} onChange={set("church_name")} className={inputCls} placeholder={f.placeholders.church} data-testid="input-church" aria-invalid={!!errors.church_name} />
                  </Field>
                  <Field id="f-role" label={f.labels.role}>
                    <input id="f-role" value={form.role} onChange={set("role")} className={inputCls} placeholder={f.placeholders.role} data-testid="input-role" />
                  </Field>
                  <Field id="f-phone" label={f.labels.phone}>
                    <input id="f-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={f.placeholders.phone} data-testid="input-phone" autoComplete="tel" />
                  </Field>
                  <Field id="f-email" label={f.labels.email} error={errors.email} hint={f.hint}>
                    <input id="f-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={f.placeholders.email} data-testid="input-email" aria-invalid={!!errors.email} autoComplete="email" />
                  </Field>
                  <Field id="f-location" label={f.labels.location}>
                    <input id="f-location" value={form.location} onChange={set("location")} className={inputCls} placeholder={f.placeholders.location} data-testid="input-location" />
                  </Field>
                  <Field id="f-language" label={f.labels.language}>
                    <select id="f-language" value={form.preferred_language} onChange={set("preferred_language")} className={inputCls} data-testid="input-language">
                      {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </Field>
                  <Field id="f-contact-method" label={f.labels.contactMethod}>
                    <select id="f-contact-method" value={form.preferred_contact} onChange={set("preferred_contact")} className={inputCls} data-testid="input-contact-method">
                      {f.contactOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                </div>

                <div>
                  <p className="text-sm font-semibold text-navy-900 mb-1.5">{f.labels.interest} <span className="text-gold-600" aria-hidden>*</span></p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {checkboxRow("interest_device", f.interestDevice, "input-interest-device")}
                    {checkboxRow("interest_website", f.interestWebsite, "input-interest-website")}
                  </div>
                  {errors.interest && <p className="mt-1 text-sm text-red-600" role="alert">{errors.interest}</p>}
                </div>

                <label htmlFor="f-optin" className="flex items-start gap-3 rounded-xl border border-navy-100 bg-[#FAFAFC] p-4 cursor-pointer hover:border-gold-500/60 transition-colors">
                  <input
                    id="f-optin" type="checkbox" checked={form.show_church_publicly}
                    onChange={(e) => setForm((fm) => ({ ...fm, show_church_publicly: e.target.checked }))}
                    data-testid="input-optin" className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37]"
                  />
                  <span className="text-sm text-charcoal-700 leading-relaxed">
                    {f.optin} <span className="text-charcoal-700/60">{f.optional}</span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="consultation-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 text-navy-900 font-semibold px-8 py-4 min-h-[48px] text-lg hover:bg-gold-400 transition-colors shadow-[0_6px_20px_-4px_rgba(212,175,55,0.55)] disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2"
                >
                  {submitting ? <><Loader2 size={20} className="animate-spin" aria-hidden /> {f.sending}</> : f.submit}
                </button>
                <p className="text-center text-xs text-charcoal-700/60">{f.privacy}</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
